import { useEffect, useMemo, useRef, useState } from 'react';

import hydrateDefaultValues from '@atlaskit/smart-user-picker/default-value-hydration-client';
import type { OptionData, OptionIdentifier, User as PickerUser } from '@atlaskit/user-picker/types';

export type HydratedFilterUsersResult = {
	users: PickerUser[];
	isLoading: boolean;
	error: Error | null;
};

// hydrateDefaultValues resolves every submitted id against the users endpoint and fills any id it
// can't match with this sentinel name (not exported from @atlaskit/smart-user-picker), rather than
// omitting it. Since most literals we submit here are filter values that aren't accountIds at all,
// we must drop sentinel-labelled entries so non-user values keep rendering raw instead of "Unknown".
const UNRESOLVED_USER_NAME = 'Unknown';

// Deliberately not wrapping @atlassian/team-custom-fields' useHydratedUsers: this package has no
// existing dependency on that (People and Teams) package, and that hook has the same cloudId-
// staleness gap fixed below, so wrapping it would either propagate the bug or require patching
// another team's code.
export function useHydratedFilterUsers(ids: string[], cloudId: string): HydratedFilterUsersResult {
	const [users, setUsers] = useState<PickerUser[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const idsKey = useMemo(() => Array.from(new Set(ids)).sort().join(','), [ids]);
	const requestKey = useMemo(() => `${idsKey}::${cloudId}`, [idsKey, cloudId]);

	const disposed = useRef(false);
	useEffect(() => {
		return () => {
			disposed.current = true;
		};
	}, []);

	const inFlightFor = useRef<string | null>(null);

	useEffect(() => {
		if (ids.length === 0) {
			inFlightFor.current = null;
			setUsers([]);
			setIsLoading(false);
			setError(null);
			return;
		}
		if (inFlightFor.current === requestKey) {
			return;
		}
		inFlightFor.current = requestKey;
		setIsLoading(true);
		setError(null);

		const userIdentifiers = ids.map((id) => ({ id, type: 'user' as const }));

		hydrateDefaultValues(undefined, userIdentifiers, 'people', cloudId)
			.then((hydratedData) => {
				if (disposed.current) return;
				if (inFlightFor.current !== requestKey) return;

				const fetchedUsers = Array.isArray(hydratedData) ? hydratedData : [hydratedData];
				const fetchedUserMap = new Map<string, PickerUser>();
				fetchedUsers.forEach((user: OptionData | OptionIdentifier | null | undefined) => {
					if (
						user &&
						'name' in user &&
						user.id &&
						user.name &&
						user.name !== UNRESOLVED_USER_NAME
					) {
						fetchedUserMap.set(user.id, {
							id: user.id,
							name: user.name,
							avatarUrl: user.avatarUrl || '',
							type: 'user' as const,
						});
					}
				});

				const orderedUsers = ids
					.map((id) => fetchedUserMap.get(id))
					.filter((user): user is PickerUser => user != null);

				setUsers(orderedUsers);
				setIsLoading(false);
				inFlightFor.current = null;
			})
			.catch((err: Error) => {
				if (disposed.current) return;
				if (inFlightFor.current === requestKey) {
					setError(err);
					setIsLoading(false);
					inFlightFor.current = null;
				}
			});

		return () => {
			inFlightFor.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- requestKey is a stable string derived from ids and cloudId, used intentionally to avoid infinite loops from array reference changes
	}, [requestKey]);

	return { users, isLoading, error };
}
